import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Calendar,
  Users,
  BarChart2,
  MessageCircle,
  Trophy,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Header } from "../../components/layout/Header";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import { Avatar } from "../../components/common/Avatar";
import { api } from "../../services/api";
import { Event, EventStatus, Group } from "../../types";
import { COLORS } from "../../config/colors";
import { formatEventDate, toDate } from "../../utils/date";

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  useEffect(() => {
    async function load() {
      try {
        const [eventsRes, groupsRes] = await Promise.all([
          api.events.list() as Promise<{ data?: Event[] }>,
          api.groups.list() as Promise<{ data?: Group[] }>,
        ]);
        const now = new Date();
        const upcoming = (eventsRes.data ?? []).filter((e) => {
          const d = e.rounds[0]?.date ? toDate(e.rounds[0].date) : null;
          return (d && d >= now) || e.status === EventStatus.IN_PROGRESS;
        });
        setUpcomingEvents(upcoming.slice(0, 3));
        setGroups(groupsRes.data ?? []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const dashboardCards = [
    { label: "Events", icon: Calendar, color: COLORS.events, path: "/events" },
    { label: "Friends", icon: Users, color: COLORS.friends, path: "/friends" },
    { label: "Stats", icon: BarChart2, color: COLORS.stats, path: "/stats" },
    { label: "Chat", icon: MessageCircle, color: COLORS.chat, path: "/chat" },
  ];

  return (
    <>
      <Header />
      <div className='px-4 py-4 space-y-4'>
        {/* Quick actions row */}
        <div className='grid grid-cols-4 gap-2'>
          {dashboardCards.map(({ label, icon: Icon, color, path }) => (
            <button
              key={label}
              onClick={() => navigate(path)}
              className='flex flex-col items-center gap-1.5 bg-white rounded-2xl p-3 shadow-sm border border-gray-100 active:scale-95 transition-transform'
            >
              <div
                className='w-10 h-10 rounded-xl flex items-center justify-center'
                style={{ backgroundColor: `${color}20` }}
              >
                <Icon size={20} style={{ color }} />
              </div>
              <span className='text-[11px] font-medium text-gray-600'>
                {label}
              </span>
            </button>
          ))}
        </div>

        {/* Upcoming Events */}
        <section>
          <div className='flex items-center justify-between mb-2'>
            <h2 className='text-base font-bold text-primary-800'>
              Upcoming Events
            </h2>
            <button
              onClick={() => navigate("/events")}
              className='text-sm text-secondary-400 font-medium'
            >
              View all
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : upcomingEvents.length === 0 ? (
            <Card className='text-center py-6'>
              <Trophy size={32} className='mx-auto text-gray-300 mb-2' />
              <p className='text-sm text-gray-500'>No upcoming events</p>
              <Button
                variant='secondary'
                size='sm'
                className='mt-3'
                onClick={() => navigate("/events/create")}
                leftIcon={<Plus size={16} />}
              >
                Create Event
              </Button>
            </Card>
          ) : (
            <div className='space-y-2'>
              {upcomingEvents.map((event) => (
                <Card
                  key={event.id}
                  accent={COLORS.events}
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  <div className='flex items-start justify-between'>
                    <div className='flex-1 min-w-0'>
                      <p className='font-semibold text-primary-800 truncate'>
                        {event.name}
                      </p>
                      <p className='text-sm text-gray-500 mt-0.5'>
                        {event.rounds[0]?.courseName ?? "TBD"}
                      </p>
                      {event.rounds[0]?.date && (
                        <p className='text-xs text-secondary-400 font-medium mt-1'>
                          {formatEventDate(event.rounds[0].date)}
                        </p>
                      )}
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ml-2 shrink-0 ${
                        event.status === EventStatus.IN_PROGRESS
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {event.status === EventStatus.IN_PROGRESS
                        ? "Live"
                        : "Upcoming"}
                    </span>
                  </div>
                  <div className='flex items-center gap-1 mt-2'>
                    <Users size={13} className='text-gray-400' />
                    <span className='text-xs text-gray-400'>
                      {event.playerIds.length} players
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* My Groups */}
        {groups.length > 0 && (
          <section>
            <div className='flex items-center justify-between mb-2'>
              <h2 className='text-base font-bold text-primary-800'>
                My Groups
              </h2>
              <button
                onClick={() => navigate("/friends")}
                className='text-sm text-secondary-400 font-medium'
              >
                View all
              </button>
            </div>
            <div className='flex gap-2 overflow-x-auto pb-1'>
              {groups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => navigate(`/friends?group=${g.id}`)}
                  className='flex-shrink-0 bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center gap-2 active:scale-95 transition-transform'
                >
                  <div className='w-8 h-8 rounded-xl bg-groups/10 flex items-center justify-center'>
                    <Users size={16} className='text-groups' />
                  </div>
                  <div className='text-left'>
                    <p className='text-xs font-semibold text-primary-800 whitespace-nowrap'>
                      {g.name}
                    </p>
                    <p className='text-[10px] text-gray-400'>
                      {g.memberIds.length} members
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Create Event FAB */}
        <div className='fixed bottom-24 right-4 z-30'>
          <button
            onClick={() => navigate("/events/create")}
            className='w-14 h-14 rounded-full bg-secondary-400 text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform hover:bg-secondary-500'
            aria-label='Create event'
          >
            <Plus size={28} />
          </button>
        </div>
      </div>
    </>
  );
}
